import React, { useEffect, useState, useRef, useCallback } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap"
import {
  GoogleMap,
  useJsApiLoader,
  Polyline,
  OverlayView,
  InfoWindow,
} from "@react-google-maps/api"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Swal from "sweetalert2"
import Loader from "components/Loader/Loader"
import { FaRoute, FaRoad, FaMoneyBillWave } from "react-icons/fa"
import { MdLocationOn } from "react-icons/md"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_ROADS_KEY

const MAP_TYPES = [
  { label: "Streets", value: "roadmap" },
  { label: "Satellite", value: "satellite" },
  { label: "Hybrid", value: "hybrid" },
  { label: "Terrain", value: "terrain" },
]

/* ================= ROUTE HELPERS ================= */

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const toRad = d => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const MIN_POINT_DIST_M = 30

function perpDist(p, a, b) {
  const dx = b[1] - a[1], dy = b[0] - a[0]
  if (!dx && !dy) return Math.hypot(p[0] - a[0], p[1] - a[1])
  const t = ((p[0] - a[0]) * dy + (p[1] - a[1]) * dx) / (dx * dx + dy * dy)
  return Math.hypot(p[0] - (a[0] + t * dy), p[1] - (a[1] + t * dx))
}

function douglasPeucker(points, tolerance) {
  if (points.length <= 2) return points
  let maxDist = 0, maxIdx = 0
  const first = points[0], last = points[points.length - 1]
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpDist(points[i], first, last)
    if (d > maxDist) { maxDist = d; maxIdx = i }
  }
  if (maxDist > tolerance) {
    const left = douglasPeucker(points.slice(0, maxIdx + 1), tolerance)
    const right = douglasPeucker(points.slice(maxIdx), tolerance)
    return [...left.slice(0, -1), ...right]
  }
  return [first, last]
}

const OVERLAP_METERS = 30
const PX_SPREAD = 70

function spreadOverlappingMarkers(markers) {
  const groups = []
  markers.forEach(m => {
    const idx = groups.findIndex(g =>
      haversineMeters(g[0].position[0], g[0].position[1], m.position[0], m.position[1]) < OVERLAP_METERS
    )
    if (idx >= 0) groups[idx].push(m)
    else groups.push([m])
  })
  const spread = []
  groups.forEach(group => {
    if (group.length === 1) {
      spread.push({ ...group[0], pxOffset: { x: 0, y: 0 } })
      return
    }
    if (group.length === 2) {
      spread.push({ ...group[0], pxOffset: { x: -PX_SPREAD, y: 0 }, _overlapped: true })
      spread.push({ ...group[1], pxOffset: { x: PX_SPREAD, y: 0 }, _overlapped: true })
      return
    }
    const total = group.length
    group.forEach((m, i) => {
      const angleDeg = -90 + (360 / total) * i
      const angleRad = (angleDeg * Math.PI) / 180
      spread.push({
        ...m,
        pxOffset: { x: PX_SPREAD * Math.cos(angleRad), y: PX_SPREAD * Math.sin(angleRad) },
        _overlapped: true,
      })
    })
  })
  return spread
}

/* ================= CUSTOM MARKER COMPONENTS ================= */

const CalloutMarker = ({ borderColor, bgColor, iconHtml, labelText, time, onClick }) => (
  <div onClick={e => { e.stopPropagation(); onClick?.(e) }} style={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "center", cursor: "pointer", userSelect: "none", pointerEvents: "auto" }}>
    <div style={{ background: "#fff", border: `2px solid ${borderColor}`, borderRadius: 10, padding: "5px 10px 5px 6px", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 4px 14px rgba(0,0,0,0.2)", whiteSpace: "nowrap", fontFamily: "sans-serif", fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
      <span style={{ width: 22, height: 22, borderRadius: "50%", background: bgColor, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, flexShrink: 0 }}>{iconHtml}</span>
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span>{labelText}</span>
        {time && <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>{time}</span>}
      </span>
    </div>
    <div style={{ width: 0, height: 0, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderTop: `7px solid ${borderColor}` }} />
  </div>
)

const StayMarker = ({ index, color, durationMins, startTime, endTime, onClick }) => {
  const isStop = durationMins >= 1
  const label = !isStop
    ? "Passing"
    : durationMins < 60
      ? `${durationMins} min stop`
      : `${fmtDur(durationMins)} stop`
  const timeText =
    startTime && endTime && startTime !== endTime
      ? `${fmt12(startTime)} – ${fmt12(endTime)}`
      : startTime ? fmt12(startTime) : null
  const handleClick = e => { e.stopPropagation(); onClick?.(e) }
  return (
    <div onClick={handleClick} style={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "center", cursor: "pointer", userSelect: "none", pointerEvents: "auto" }}>
      <div style={{ background: "#fff", border: `2px solid ${color}`, borderRadius: 10, padding: "4px 9px 4px 5px", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 4px 14px rgba(0,0,0,0.22)", whiteSpace: "nowrap", fontFamily: "sans-serif", fontSize: 11, fontWeight: 700, color: "#1e293b" }}>
        <span style={{ width: 20, height: 20, borderRadius: "50%", background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, flexShrink: 0 }}>{index}</span>
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
          <span style={{ color: isStop ? color : "#94a3b8", fontStyle: isStop ? "normal" : "italic" }}>{label}</span>
          {timeText && <span style={{ fontSize: 9, fontWeight: 600, color: "#64748b" }}>{timeText}</span>}
        </span>
      </div>
      <div style={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: `6px solid ${color}` }} />
    </div>
  )
}

const LiveMarker = ({ time, onClick }) => (
  <div onClick={e => { e.stopPropagation(); onClick?.(e) }} style={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "center", cursor: "pointer", userSelect: "none", pointerEvents: "auto" }}>
    <div style={{ background: "#fff", border: "2px solid #3b82f6", borderRadius: 10, padding: "5px 12px 5px 8px", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 4px 14px rgba(59,130,246,0.3)", whiteSpace: "nowrap", fontFamily: "sans-serif", fontSize: 12, fontWeight: 800, color: "#3b82f6" }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#3b82f6", boxShadow: "0 0 0 3px rgba(59,130,246,0.25)", flexShrink: 0, display: "inline-block" }} />
      <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span>LIVE</span>
        {time && <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>{time}</span>}
      </span>
    </div>
    <div style={{ width: 0, height: 0, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderTop: "7px solid #3b82f6" }} />
  </div>
)

/* ================= STAT CARD ================= */

const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, border: `1px solid ${color}22`, boxShadow: `0 2px 8px ${color}12` }}>
    <div style={{ width: 28, height: 28, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color, flexShrink: 0 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>{value}</div>
    </div>
  </div>
)

/* ================= PUNCH TIMELINE ================= */

const PunchTimeline = ({ attendance, onFlyTo }) => {
  if (!attendance?.length) return null
  const entries = []
  attendance.forEach(item => {
    if (item.in_latitude)
      entries.push({
        type: "in",
        time: item.inTime,
        address: item.punchInAddress,
        lat: parseFloat(item.in_latitude),
        lng: parseFloat(item.in_longitude),
      })
    if (item.activePunch)
      entries.push({
        type: "live",
        time: item.outTime,
        address: item.punchOutAddress,
        lat: item.out_latitude ? parseFloat(item.out_latitude) : null,
        lng: item.out_longitude ? parseFloat(item.out_longitude) : null,
      })
    else if (item.outTime)
      entries.push({
        type: "out",
        time: item.outTime,
        address: item.punchOutAddress || null,
        lat: item.out_latitude ? parseFloat(item.out_latitude) : null,
        lng: item.out_longitude ? parseFloat(item.out_longitude) : null,
      })
  })
  const cfg = {
    in: { color: "#22c55e", icon: "✓", label: "Punch In" },
    live: { color: "#3b82f6", icon: "●", label: "LIVE" },
    out: { color: "#EF5350", icon: "✕", label: "Punch Out" },
  }
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 16 }}>Punch Timeline</div>
      {entries.map((entry, i) => {
        const c = cfg[entry.type]
        const canFly = entry.lat != null && entry.lng != null && onFlyTo
        return (
          <div key={i}>
            <div
              onClick={canFly ? () => onFlyTo(entry.lat, entry.lng) : undefined}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                cursor: canFly ? "pointer" : "default",
                padding: "4px 6px",
                margin: "-4px -6px",
                borderRadius: 8,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => { if (canFly) e.currentTarget.style.background = `${c.color}10` }}
              onMouseLeave={e => { if (canFly) e.currentTarget.style.background = "transparent" }}
            >
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: c.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>{c.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: c.color }}>{c.label}</span>
                  <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{entry.time || "—"}</span>
                </div>
                {entry.address && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3, lineHeight: 1.5 }}>{entry.address}</div>}
              </div>
            </div>
            {i < entries.length - 1 && <div style={{ marginLeft: 15, marginTop: 4, marginBottom: 4, borderLeft: "2px dashed #e2e8f0", height: 16 }} />}
          </div>
        )
      })}
    </div>
  )
}

/* ================= LOCATION STAY TIMELINE ================= */

const STAY_COLORS = ["#1a73e8", "#34a853", "#fa7b17", "#ea4335", "#9334e6", "#00897b", "#e91e63", "#f09300"]
const toMins = t => { const [h, m] = t.split(":").map(Number); return h * 60 + m }

function buildStayTimeline(coordinates, attendance, radiusMeters = 150) {
  if (!coordinates?.length) return []
  const sorted = [...coordinates].sort((a, b) => a.time.localeCompare(b.time))
  const lastAttendance = attendance?.[attendance.length - 1]
  const impliedEndTime =
    lastAttendance && !lastAttendance.out_latitude && !lastAttendance.activePunch
      ? sorted[sorted.length - 1].time.slice(0, 5) : null
  const groups = []
  let group = [sorted[0]]
  for (let i = 1; i < sorted.length; i++) {
    const anchor = group[0], curr = sorted[i]
    if (haversineMeters(anchor.latitude, anchor.longitude, curr.latitude, curr.longitude) <= radiusMeters) {
      group.push(curr)
    } else { groups.push(group); group = [curr] }
  }
  groups.push(group)
  return groups.map((points, i) => {
    const startTime = points[0].time.slice(0, 5)
    const endTime = points[points.length - 1].time.slice(0, 5)
    const isLast = i === groups.length - 1
    return { locIndex: i + 1, startTime, endTime, durationMins: Math.max(0, toMins(endTime) - toMins(startTime)), lat: points[0].latitude, lng: points[0].longitude, pointCount: points.length, impliedEnd: isLast && impliedEndTime ? impliedEndTime : null }
  })
}

const fmtDur = mins => {
  if (mins < 1) return "< 1 min"
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60), m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

const fmt12 = t => {
  const [h, m] = t.split(":").map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`
}

const LocationTimeline = ({ coordinates, attendance, onFlyTo }) => {
  const segments = buildStayTimeline(coordinates, attendance)
  if (!segments.length) return null
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
        <div style={{ width: 4, height: 18, background: "#1a73e8", borderRadius: 2 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>Location Timeline</span>
      </div>
      {segments.map((seg, i) => {
        const color = STAY_COLORS[(seg.locIndex - 1) % STAY_COLORS.length]
        const isLast = i === segments.length - 1
        const nextSeg = segments[i + 1]
        const transitMins = nextSeg ? Math.max(0, toMins(nextSeg.startTime) - toMins(seg.endTime)) : 0
        const displayEnd = seg.impliedEnd || seg.endTime
        return (
          <div key={i}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 2 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, boxShadow: `0 2px 8px ${color}55` }}>{seg.locIndex}</div>
                {!isLast && <div style={{ width: 2, minHeight: 48, background: "#e2e8f0", margin: "4px 0", borderRadius: 1 }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: isLast ? 0 : 4 }}>
                <div
                  onClick={() => onFlyTo?.(seg.lat, seg.lng)}
                  style={{ cursor: "pointer", background: "#fff", borderRadius: 12, padding: "10px 12px", marginBottom: 2, border: `1.5px solid ${color}30`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow 0.15s, border-color 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 3px 12px ${color}30`; e.currentTarget.style.borderColor = `${color}88` }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.borderColor = `${color}30` }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                      {fmt12(seg.startTime)}
                      {seg.startTime !== displayEnd && <span> – {fmt12(displayEnd)}</span>}
                      {seg.impliedEnd && <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 400 }}> *</span>}
                    </span>
                    {seg.durationMins > 0
                      ? <span style={{ fontSize: 11, background: `${color}15`, color, borderRadius: 20, padding: "2px 8px", fontWeight: 700 }}>{seg.durationMins < 60 ? `${seg.durationMins} min stop` : fmtDur(seg.durationMins)}</span>
                      : <span style={{ fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>passing</span>
                    }
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color, fontSize: 10 }}>📍</span>
                    {seg.lat.toFixed(5)}, {seg.lng.toFixed(5)}
                    <span style={{ color: "#cbd5e1", marginLeft: 4 }}>· {seg.pointCount} pts</span>
                  </div>
                </div>
                {!isLast && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px" }}>
                    <span style={{ fontSize: 13 }}>🚗</span>
                    <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>In transit</span>
                    {transitMins > 0 && <span style={{ fontSize: 11, color: "#94a3b8" }}>· {fmtDur(transitMins)}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
      {segments.some(s => s.impliedEnd) && (
        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 10, paddingLeft: 44 }}>
          * No punch-out recorded — last GPS point used as end time
        </div>
      )}
    </div>
  )
}

/* ================= LEGEND DOT ================= */

const LegendDot = ({ color, label, isLine, dark }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
    {isLine
      ? <div style={{ width: 18, height: 3, background: color, borderRadius: 2 }} />
      : <div style={{ width: 9, height: 9, borderRadius: "50%", background: color }} />
    }
    <span style={{ fontSize: 11, fontWeight: 500, color: dark ? "#cbd5e1" : "#64748b" }}>{label}</span>
  </div>
)

/* ================= OVERLAY POSITION HELPER ================= */

const markerOffset = (w, h) => ({ x: -(w / 2), y: -h })

/* ================= MAIN COMPONENT ================= */

const LocationTracking = () => {
  document.title = "Location Tracking | Vintage Infra Equipment Sol PVT LTD"
  const [employee, setEmployee] = useState("")
  const [date, setDate] = useState("")
  const [route, setRoute] = useState([])
  const [data, setData] = useState({})
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [popupInfo, setPopupInfo] = useState(null)
  const [mapCenter, setMapCenter] = useState({ lat: 19.076, lng: 72.8777 })
  const [mapZoom, setMapZoom] = useState(12)
  const [mapType, setMapType] = useState("roadmap")
  const mapRef = useRef(null)
  const geocodeCacheRef = useRef({})
  const [mapReady, setMapReady] = useState(false)

  const { checkAccess } = usePermissionData()

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_KEY,
  })

  const onMapLoad = useCallback(map => {
    mapRef.current = map
    setMapReady(true)
  }, [])

  useEffect(() => { getEmployees() }, [])

  const getEmployees = async () => {
    const res = await fetchData(setLoading, "users")
    if (res?.success) setEmployees(res?.data)
  }

  const fetchLocationData = async () => {
    const res = await FetchPostData(setLoading, "attendance/user/route/by/date", { userId: employee, date })
    if (res?.data?.success) {
      setData(res?.data)
      const pts = Object.values(res?.data?.data?.coordinates || {}).sort((a, b) => a.id - b.id)
      const filteredPts = []
      for (let i = 0; i < pts.length; i++) {
        if (i > 0 && pts[i].time < pts[i - 1].time) break
        if (pts[i].accuracy != null && pts[i].accuracy > 150) continue
        filteredPts.push(pts[i])
      }
      setRoute(filteredPts)
    } else {
      Swal.fire("Error", res?.response?.data?.message, "error")
    }
  }

  // Fit map to route + punch markers after data loads (also re-fits once map becomes ready)
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google) return
    const bounds = new window.google.maps.LatLngBounds()
    let hasPoint = false
    route.forEach(p => {
      bounds.extend({ lat: p.latitude, lng: p.longitude })
      hasPoint = true
    })
    data?.attendance?.forEach(item => {
      if (item.in_latitude && item.in_longitude) {
        bounds.extend({ lat: parseFloat(item.in_latitude), lng: parseFloat(item.in_longitude) })
        hasPoint = true
      }
      if (item.out_latitude && item.out_longitude) {
        bounds.extend({ lat: parseFloat(item.out_latitude), lng: parseFloat(item.out_longitude) })
        hasPoint = true
      }
    })
    if (!hasPoint) return
    mapRef.current.fitBounds(bounds, 50)
  }, [route, data, mapReady])

  // Split route into continuous segments (gap > 2 min = new segment)
  const GAP_MS = 2 * 60 * 1000
  const timeMs = t => { const [h, m, s] = t.split(":").map(Number); return (h * 3600 + m * 60 + s) * 1000 }

  const routeSegs = []
  let curSeg = []
  for (let i = 0; i < route.length; i++) {
    if (i === 0 || timeMs(route[i].time) - timeMs(route[i - 1].time) > GAP_MS) {
      if (curSeg.length) routeSegs.push(curSeg)
      curSeg = [route[i]]
    } else {
      curSeg.push(route[i])
    }
  }
  if (curSeg.length) routeSegs.push(curSeg)

  // Smooth each segment and convert to Google Maps LatLng format { lat, lng }
  const googleSegments = routeSegs.map(seg =>
    douglasPeucker(
      seg
        .filter((p, i) => {
          if (i === 0) return true
          const prev = seg[i - 1]
          return haversineMeters(prev.latitude, prev.longitude, p.latitude, p.longitude) >= MIN_POINT_DIST_M
        })
        .map(p => [p.latitude, p.longitude]),
      0.00005
    ).map(([lat, lng]) => ({ lat, lng }))
  ).filter(seg => seg.length > 0)

  const hasRoute = googleSegments.length > 0

  const routeDistanceKm = (() => {
    if (!hasRoute) return null
    let meters = 0
    for (const seg of googleSegments)
      for (let i = 1; i < seg.length; i++)
        meters += haversineMeters(seg[i - 1].lat, seg[i - 1].lng, seg[i].lat, seg[i].lng)
    return (meters / 1000).toFixed(2)
  })()

  const totalTA = data?.expenses?.find(e => e?.type === "travel")?.value || 0
  const totalDA = data?.expenses?.find(e => e?.type === "dearness")?.value || 0

  const rawPunchMarkers =
    data?.attendance?.flatMap(item => {
      const arr = []
      if (item.in_latitude && item.in_longitude)
        arr.push({ type: "in", position: [parseFloat(item.in_latitude), parseFloat(item.in_longitude)], time: item.inTime, address: item.punchInAddress })
      if (item.activePunch && item.out_latitude && item.out_longitude)
        arr.push({ type: "live", position: [parseFloat(item.out_latitude), parseFloat(item.out_longitude)], time: item.outTime, address: item.punchOutAddress })
      else if (!item.activePunch && item.out_latitude && item.out_longitude)
        arr.push({ type: "out", position: [parseFloat(item.out_latitude), parseFloat(item.out_longitude)], time: item.outTime, address: item.punchOutAddress })
      return arr
    }) || []

  const punchMarkers = spreadOverlappingMarkers(rawPunchMarkers)

  const staySegments = buildStayTimeline(data?.data?.coordinates, data?.attendance)

  const lastAttendance = data?.attendance?.[data?.attendance?.length - 1]
  const hasNoPunchOut = lastAttendance && !lastAttendance.out_latitude && !lastAttendance.activePunch
  const liveFromCoord =
    hasNoPunchOut && data?.data?.coordinates?.length > 0
      ? [...data.data.coordinates].sort((a, b) => a.time.localeCompare(b.time)).at(-1)
      : null

  const reverseGeocode = (lat, lng) => {
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`
    if (geocodeCacheRef.current[key]) return Promise.resolve(geocodeCacheRef.current[key])
    if (!window.google?.maps?.Geocoder) return Promise.resolve(null)
    return new Promise(resolve => {
      new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
        const addr = status === "OK" && results?.[0]?.formatted_address ? results[0].formatted_address : null
        geocodeCacheRef.current[key] = addr
        resolve(addr)
      })
    })
  }

  const openStayPopup = (seg, timeText, durLabel) => {
    const info = {
      position: [seg.lat, seg.lng],
      time: timeText,
      address: `Location ${seg.locIndex} · ${durLabel} · ${seg.pointCount} pts`,
      placeName: "Loading address…",
      _stayKey: `${seg.locIndex}-${seg.lat}-${seg.lng}`,
    }
    setPopupInfo(info)
    reverseGeocode(seg.lat, seg.lng).then(addr => {
      setPopupInfo(prev =>
        prev && prev._stayKey === info._stayKey
          ? { ...prev, placeName: addr || "Address not available" }
          : prev
      )
    })
  }

  const flyTo = (lat, lng) => {
    if (mapRef.current) {
      mapRef.current.panTo({ lat, lng })
      mapRef.current.setZoom(17)
    }
  }

  return (
    <PermittedComponent isAccess={checkAccess(7, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Employee Tracking" breadcrumbItem="Location Tracking" />

        {hasRoute ? (
          <Row className="g-3" style={{ alignItems: "stretch" }}>
            {/* ── MAP ── */}
            <Col md={8} style={{ display: "flex", flexDirection: "column" }}>
              <Card style={{ borderRadius: 16, border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.08)", overflow: "hidden", flex: 1 }}>
                {/* Header with controls */}
                <div style={{ padding: "12px 20px", background: "linear-gradient(135deg, #1e293b, #334155)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <MdLocationOn size={17} color="#6366f1" style={{ flexShrink: 0 }} />
                  <Input type="select" value={employee} onChange={e => setEmployee(e.target.value)}
                    style={{ width: 170, height: 34, fontSize: 12, borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.1)", color: "#fff", paddingLeft: 10 }}>
                    <option value="" style={{ color: "#1e293b" }}>Select Employee</option>
                    {employees.map(emp => <option key={emp.id} value={emp.id} style={{ color: "#1e293b" }}>{emp.name}</option>)}
                  </Input>
                  <Input type="date" value={date} onChange={e => setDate(e.target.value)}
                    style={{ width: 148, height: 34, fontSize: 12, borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.1)", color: "#fff", paddingLeft: 10, colorScheme: "dark" }} />
                  <Button disabled={!employee || !date} onClick={fetchLocationData} className="btn-blue"
                    style={{ height: 34, fontSize: 12, paddingLeft: 14, paddingRight: 14 }}>
                    <FaRoute size={12} style={{ marginRight: 6 }} />
                    Track Route
                  </Button>
                  <Input type="select" value={mapType} onChange={e => setMapType(e.target.value)}
                    style={{ width: 140, height: 34, fontSize: 12, borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.1)", color: "#fff", paddingLeft: 10 }}>
                    {MAP_TYPES.map(s => <option key={s.value} value={s.value} style={{ color: "#1e293b" }}>{s.label}</option>)}
                  </Input>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 14, alignItems: "center" }}>
                    <LegendDot color="#22c55e" label="Start" dark />
                    <LegendDot color="#EF5350" label="End" dark />
                    <LegendDot color="#6366f1" label="Route" dark isLine />
                  </div>
                </div>

                {/* Google Map */}
                <div style={{ height: 560 }}>
                  {isLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={mapZoom}
                      onLoad={onMapLoad}
                      options={{
                        mapTypeId: mapType,
                        streetViewControl: true,
                        mapTypeControl: false,
                        fullscreenControl: true,
                        zoomControl: true,
                        gestureHandling: "greedy",
                        clickableIcons: false,
                      }}
                    >
                      {/* Route polylines — one per segment */}
                      {googleSegments.map((seg, i) => (
                        <Polyline
                          key={i}
                          path={seg}
                          options={{ strokeColor: "#6366f1", strokeWeight: 5, strokeOpacity: 0.85, geodesic: true }}
                        />
                      ))}

                      {/* Stay/stop markers from location timeline (stops longer than 5 min only) */}
                      {staySegments.filter(seg => seg.durationMins > 5).map((seg, i) => {
                        const color = STAY_COLORS[(seg.locIndex - 1) % STAY_COLORS.length]
                        const endTime = seg.impliedEnd || seg.endTime
                        const durLabel =
                          seg.durationMins < 60
                            ? `${seg.durationMins} min stop`
                            : `${fmtDur(seg.durationMins)} stop`
                        const timeText =
                          seg.startTime !== endTime
                            ? `${fmt12(seg.startTime)} – ${fmt12(endTime)}`
                            : fmt12(seg.startTime)
                        return (
                          <OverlayView
                            key={`stay-${i}`}
                            position={{ lat: seg.lat, lng: seg.lng }}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            getPixelPositionOffset={markerOffset}
                          >
                            <StayMarker
                              index={seg.locIndex}
                              color={color}
                              durationMins={seg.durationMins}
                              startTime={seg.startTime}
                              endTime={endTime}
                              onClick={() => openStayPopup(seg, timeText, durLabel)}
                            />
                          </OverlayView>
                        )
                      })}

                      {/* Punch in/out/live markers */}
                      {punchMarkers.map((m, i) => {
                        const pos = { lat: m.position[0], lng: m.position[1] }
                        const dx = m.pxOffset?.x || 0
                        const dy = m.pxOffset?.y || 0
                        const offsetFn = (w, h) => ({ x: -(w / 2) + dx, y: -h + dy })
                        const marker =
                          m.type === "in" ? <CalloutMarker borderColor="#22c55e" bgColor="#22c55e" iconHtml="✓" labelText="Punch In" time={m.time} onClick={() => setPopupInfo(m)} />
                          : m.type === "out" ? <CalloutMarker borderColor="#EF5350" bgColor="#EF5350" iconHtml="✕" labelText="Punch Out" time={m.time} onClick={() => setPopupInfo(m)} />
                          : <LiveMarker time={m.time} onClick={() => setPopupInfo(m)} />
                        return (
                          <OverlayView key={i} position={pos} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={offsetFn}>
                            {marker}
                          </OverlayView>
                        )
                      })}

                      {/* Live position from last coordinate */}
                      {liveFromCoord && (
                        <OverlayView
                          position={{ lat: parseFloat(liveFromCoord.latitude), lng: parseFloat(liveFromCoord.longitude) }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                          getPixelPositionOffset={markerOffset}
                        >
                          <LiveMarker
                            time={liveFromCoord.time.slice(0, 5)}
                            onClick={() => setPopupInfo({ position: [parseFloat(liveFromCoord.latitude), parseFloat(liveFromCoord.longitude)], time: liveFromCoord.time.slice(0, 5), address: "Last known location" })}
                          />
                        </OverlayView>
                      )}

                      {/* Popup on marker click */}
                      {popupInfo && (
                        <InfoWindow
                          position={{ lat: popupInfo.position[0], lng: popupInfo.position[1] }}
                          onCloseClick={() => setPopupInfo(null)}
                          options={{ pixelOffset: new window.google.maps.Size(0, -55), disableAutoPan: false }}
                        >
                          <div style={{ fontSize: 12, maxWidth: 260, fontFamily: "sans-serif" }}>
                            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: "#1e293b" }}>{popupInfo.time || "—"}</div>
                            <div style={{ color: "#475569", lineHeight: 1.5 }}>{popupInfo.address || "No address"}</div>
                            {popupInfo.placeName && (
                              <div style={{ marginTop: 6, color: "#334155", fontSize: 11, lineHeight: 1.5, display: "flex", gap: 4 }}>
                                <span style={{ color: "#6366f1" }}>📍</span>
                                <span>{popupInfo.placeName}</span>
                              </div>
                            )}
                            {popupInfo._overlapped && <div style={{ marginTop: 6, color: "#94a3b8", fontSize: 10 }}>ℹ️ Slightly offset — overlapping punch location</div>}
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 14 }}>
                      Loading map…
                    </div>
                  )}
                </div>

                {/* Bottom legend */}
                <div style={{ padding: "10px 20px", background: "#f8fafc", display: "flex", gap: 20, justifyContent: "center", borderTop: "1px solid #e2e8f0" }}>
                  <LegendDot color="#22c55e" label="Punch In" />
                  <LegendDot color="#3b82f6" label="Live Location" />
                  <LegendDot color="#EF5350" label="Punch Out" />
                </div>
              </Card>
            </Col>

            {/* ── RIGHT PANEL ── */}
            <Col md={4} style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ maxHeight: 660, overflowY: "auto", scrollbarWidth: "thin", display: "flex", flexDirection: "column", gap: 0, paddingRight: 2 }}>
                <Card className="mb-3" style={{ borderRadius: 16, border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
                  <CardBody style={{ padding: 20 }}>
                    <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
                      <StatCard icon={<FaRoad />} label="Distance" value={`${routeDistanceKm ?? data?.data?.distance?.km ?? "—"} km`} color="#6366f1" />
                      <StatCard icon={<FaMoneyBillWave />} label="TA" value={`₹${totalTA}`} color="#22c55e" />
                      <StatCard icon={<FaMoneyBillWave />} label="DA" value={`₹${totalDA}`} color="#f59e0b" />
                    </div>
                  </CardBody>
                </Card>
                <Card className="mb-3" style={{ borderRadius: 16, border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
                  <CardBody style={{ padding: 20 }}>
                    <PunchTimeline attendance={data?.attendance} onFlyTo={flyTo} />
                  </CardBody>
                </Card>
                <Card style={{ borderRadius: 16, border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
                  <CardBody style={{ padding: 20 }}>
                    <LocationTimeline
                      coordinates={data?.data?.coordinates}
                      attendance={data?.attendance}
                      onFlyTo={flyTo}
                    />
                  </CardBody>
                </Card>
              </div>
            </Col>
          </Row>
        ) : (
          /* ── EMPTY STATE ── */
          <>
            <Card style={{ borderRadius: 16, border: "none", boxShadow: "0 2px 16px rgba(99,102,241,0.08)", marginBottom: 20 }}>
              <CardBody style={{ padding: "18px 24px" }}>
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Input type="select" className="form-select rounded-4" onChange={e => setEmployee(e.target.value)}>
                      <option value="">Select Employee</option>
                      {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Input type="date" className="rounded-4" onChange={e => setDate(e.target.value)} />
                  </Col>
                  <Col md={2}>
                    <Button disabled={!employee || !date} onClick={fetchLocationData} className="btn-blue">
                      <FaRoute size={14} style={{ marginRight: 6 }} />
                      Track Route
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            <Card style={{ borderRadius: 16, border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
              <CardBody>
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
                  <FaRoute size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>No Route Found</div>
                  <div style={{ fontSize: 13 }}>Select an employee and date, then click Track Route.</div>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </PermittedComponent>
  )
}

export default LocationTracking
